import { buildCartesianAggregationLegendLabelHtml, chartHasComparisonLegendRows, resolveLegendSeriesUserOptions } from '../../../utils/chart-legend-scale.util';
import { resolveCssNumber } from '../../../utils/resolve-highcharts-css-colors.util';
import { JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS } from '../../../utils/highcharts-datetime.util';
import Highcharts from '../highcharts.config';

const THEMED_FONT_FAMILY =
	"var(--font-family, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)";

const formatNumber = (value: number) => {
	// add commas and make decimal optional, only show if it's not 0
	return value
		.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
		.replace(/\.00$/, '');
};

const escapeHtml = (value: unknown) => {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

export const JeptoColumnChartDefaults: Highcharts.Options = {
	chart: {
		type: 'column',
		animation: true,
		style: {
			fontFamily: THEMED_FONT_FAMILY,
		},
		spacing: [10, 20, 10, 20],
	},
	title: {
		text: undefined,
	},
	credits: {
		enabled: false,
	},
	xAxis: {
		type: 'datetime',
		crosshair: {
			width: 1,
			color: 'var(--border-primary-color)',
		},
		labels: {
			style: {
				color: 'var(--text-primary-color)',
			},
		},
		gridLineWidth: 0,
		tickLength: 0,
		dateTimeLabelFormats: JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS,
	},
	yAxis: {
		title: {
			text: undefined,
		},
		labels: {
			style: {
				color: 'var(--text-primary-color)',
			},
		},
		gridLineColor: 'var(--border-primary-color)',
		gridLineDashStyle: 'Dot',
	},
	plotOptions: {
		column: {
			borderRadius: 3,
			borderWidth: 0,
			groupPadding: 0.2,
			pointPadding: 0.1,
			states: {
				hover: {
					brightness: 0.1,
					borderColor: 'var(--border-primary-color)',
				},
			},
			dataLabels: {
				enabled: false,
				// Explicit color — Highcharts `contrast` vs CSS-var chart bg is transparent
				style: {
					color: 'var(--text-primary-color)',
					textOutline: '1px var(--card-background-color, #fff)',
					fontSize: '11px',
				},
			},
		},
		series: {
			states: {
				inactive: {
					opacity: 0.5,
				},
			},
			stacking: undefined,
		},
	},
	tooltip: {
		outside: true,
		enabled: true,
		shared: true,
		useHTML: true,
		shadow: {
			color: 'var(--card-shadow-color)',
			opacity: 1,
			offsetX: 0,
			offsetY: 4,
			width: 12,
		},
		backgroundColor: 'var(--background-primary-color)',
		borderColor: 'var(--border-primary-color)',
		borderWidth: resolveCssNumber('--card-border-width', 1),
		borderRadius: resolveCssNumber('--card-border-radius', 6),
		formatter: function (this: any) {
			const points = this.points || [];
			// For column charts with categories, this.x is the category index (number)
			// We need to look up the actual category name from the xAxis categories
			const xAxis = this.points?.[0]?.series?.xAxis;
			const categories = xAxis?.categories;
			const category =
				categories && typeof this.x === 'number'
					? categories[this.x]
					: this.x;
			const safeCategory = escapeHtml(category);
			let tooltipContent = `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: var(--text-secondary-color); margin-bottom: 5px;">${safeCategory}</div>`;

			// Helper function to get column style - uses the series color
			const getColumnStyle = (point: any) => {
				return `background-color: ${point.series.color}`;
			};

			points.forEach((point: any) => {
				const safePointSeriesName = escapeHtml(point.series.name);
				// Build tooltip content
				tooltipContent += `
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <div style="width: 3px; height: 30px; ${getColumnStyle(
						point
					)}; margin-right: 8px;"></div>
                    <div style="display: flex; flex-direction: column;">
                        <span style="color: var(--text-secondary-color); font-size: var(--font-size-xs); font-weight: var(--font-weight-regular);">${safePointSeriesName}</span>
                        <div style="color: var(--text-primary-color); font-weight: var(--font-weight-regular); font-size: var(--font-size-sm)">
                            ${formatNumber(point.y)}
                        </div>
                    </div>
                </div>`;
			});

			return `<div style="padding: 5px; background: var(--background-primary-color);">${tooltipContent}</div>`;
		},
		style: {
			color: 'var(--text-primary-color)',
			fontSize: 'var(--font-size-sm)',
		},
	},
	legend: {
		enabled: true,
		align: 'left',
		verticalAlign: 'top',
		layout: 'horizontal',
		useHTML: true,
		labelFormatter: function () {
			const seriesRef = this as any;
			const options = resolveLegendSeriesUserOptions(seriesRef);
			const columnStyle = `background-color: ${this.color}`;
			const chart = seriesRef.chart;

			return buildCartesianAggregationLegendLabelHtml({
				seriesName: escapeHtml(this.name),
				swatchInlineStyle: columnStyle,
				aggregationValue: options.aggregationValue,
				comparisonAggregationValue: options.comparisonAggregationValue,
				comparisonData: options.comparisonData,
				formatNumber,
				fontFamily: THEMED_FONT_FAMILY,
				comparisonRowEnabled: chartHasComparisonLegendRows(chart?.series),
			});
		},
		itemStyle: {
			color: 'var(--text-primary-color)',
			fontWeight: 'normal',
			fontSize: 'var(--font-size-md)',
			fontFamily: THEMED_FONT_FAMILY,
		},
		itemHoverStyle: {
			color: 'var(--text-primary-color)',
		},
		symbolHeight: 0,
		symbolWidth: 0,
		symbolRadius: 0,
		symbolPadding: 4,
		itemDistance: 12,
		alignColumns: true,
		itemMarginBottom: 2,
		navigation: {
			activeColor: 'var(--text-link-color)',
			inactiveColor: 'var(--text-secondary-color)',
			animation: true,
		},
	},
};
